/**
 * Raise and clear dwells for client-derived attention items (twin slice
 * 1, plan W5 step 11; ideation §7 "Debounce").
 *
 *   RAISE  only after the condition has held for 10 s ACROSS AT LEAST TWO
 *          READINGS: the first reading that showed it and a later one at
 *          least 10 s after it, with no reading in between that did not.
 *          One reading followed by silence never raises, however long the
 *          silence -- silence is not evidence.
 *   CLEAR  only after 30 s of readings that no longer show it (two issue
 *          polls). A reading that shows it again inside those 30 s keeps
 *          the item up, so a flapping `lanes_closed` bit does not churn.
 *   CLEARING  while those 30 s run, the item is still on screen: it
 *          "looks fixed" from the first reading that no longer showed it
 *          and is confirming (amendment A4, 2026-10-04). Before A4 the
 *          view said nothing between "up" and "Cleared at", so a card
 *          vanished for the whole dwell and then came back. A reading
 *          that shows it again inside the dwell makes it simply up again,
 *          the same run, never "Cleared at" in between. Silence never
 *          completes a clear: it stays "looks fixed" until a reading 30 s
 *          on says so.
 *   CLEARED AT  the FIRST reading that no longer showed it, kept on screen
 *          for 2 minutes from that time (Decision 9) -- but never while the
 *          condition is showing again: a key that comes back inside the 2
 *          minutes is absent until it raises again, because "Cleared at"
 *          over a condition that is present would be false.
 *   CAN'T READ IT  (amendment A7, 2026-10-04) a reading may say which of
 *          its source's keys it cannot speak for (`cannotRead`): a car
 *          with no reading, a robot whose presence is lost, a whole source
 *          that did not read. Not showing such a key is NOT a reading that
 *          no longer shows it -- an unreadable car is not "fixed". A raised
 *          key stays up: the reading neither starts its clear dwell nor
 *          advances one, and a dwell already running is void -- the run of
 *          readings that looked fixed was broken, so the 30 s start again
 *          from the next reading that can speak for it and no longer shows
 *          it. The view says since when it could not be read
 *          (`unreadSinceMs`). A key not raised is left as it is:
 *          the reading neither shows it nor contradicts it, so a run still
 *          dwelling neither breaks nor counts it, and a "Cleared at" that a
 *          completed dwell confirmed stays a past fact. Before A7 a carded
 *          car that lost its reading read "Looks fixed, confirming…" for
 *          the 10 s until "No reading" raised, and "Cleared at" after 30 s
 *          of a clamped feed.
 *          A reading may also SHOW a key it cannot vouch for (A17b): a
 *          robot gone dark whose registry still lists its report. Shown,
 *          it is up as any shown key is (any clear dwell void), but the
 *          view still says since when it could not be read, and the entry
 *          keeps what the last reading that could vouch for it said it was
 *          about -- so a reader holding a card "as it was when it went
 *          dark" (A11) has both, whether or not the report is still listed.
 *          What can be read may also change with no reading at all -- a
 *          robot going dark on the clock -- and a GLANCE (`glance`) dates
 *          the hold then, without reading anything. And a spell that ends
 *          on the same data brings nothing new about a key it can speak for
 *          again (`stale`): that key is folded as a glance folds it, so the
 *          data a held run began with is still its only reading (W5G
 *          review, after A19).
 *
 * WHAT IT WAS. A clearing or cleared condition is no longer in the live
 * reading, so the reader cannot word its card from that reading. Each
 * entry keeps the `about` the last reading that showed it handed over (the
 * selector's subject and copy), and the view returns it -- with when the
 * run it ended began (`seenSinceMs`), so a reader whose own card waits
 * longer than the raise (a lift held 5 min) can tell whether it was ever
 * up.
 *
 * Alert-backed items do not come through here: they follow their alert.
 *
 * All times are READING times on the device clock: when this page received
 * the reading, not any stamp inside it. A reading is counted once; the same
 * reading handed over twice (a re-render) is not a second reading.
 *
 * Pure and immutable: `advance` returns a new state, and `debouncedView`
 * reads one. The hook that owns the state (wave B) feeds it per source.
 */

export const RAISE_AFTER_MS = 10_000;
export const CLEAR_AFTER_MS = 30_000;
export const CLEARED_SHOWN_FOR_MS = 120_000;

interface Entry<A> {
  /** The source whose readings decide this key. */
  source: string;
  /** What the last reading that showed it said it was about. */
  about: A | undefined;
  /** Start of the current run of readings that showed it. */
  firstTrueAt: number | null;
  raised: boolean;
  /** First reading, while raised, that no longer showed it. */
  firstFalseAt: number | null;
  /** The completed clear: the first reading of the run that cleared it. */
  clearedAt: number | null;
  /** Start of the run that the completed clear ended. */
  clearedRunFrom: number | null;
  /** While raised: the first of the readings since that could not speak
   *  for it (A7). Null once a reading can. */
  unreadAt: number | null;
}

export interface DebounceState<A = unknown> {
  readonly entries: ReadonlyMap<string, Entry<A>>;
  /** Last reading (or glance) time accepted per source. */
  readonly lastReadingAt: ReadonlyMap<string, number>;
}

export const EMPTY_DEBOUNCE: DebounceState<never> = { entries: new Map(), lastReadingAt: new Map() };

export interface Reading<A = unknown> {
  /** e.g. `robots`, `lifts`: each source advances only its own keys. */
  source: string;
  /** When this page received the reading (device clock). */
  atMs: number;
  /** The keys whose condition this reading shows, each with what it is
   *  about (a plain set when nothing needs remembering). */
  active: ReadonlySet<string> | ReadonlyMap<string, A>;
  /** Keys of this source this reading cannot speak for (A7): what they
   *  are about can't be read now. Asked of every key of the source: one
   *  it does not show is held, one it shows while it cannot vouch for it
   *  is stamped and keeps its last vouched `about` (A17b). Absent: it can
   *  speak for every key. */
  cannotRead?: (key: string) => boolean;
  /** Keys of this source it CAN speak for but brings no new data about:
   *  the data it was read from is the data they were last read from, and
   *  only a spell in which they could not be read came between (the hook
   *  decides, `AttentionBook`). Folded as a glance folds a key it can read:
   *  a raised one loses its unread stamp, and nothing else moves -- no run
   *  starts or grows and no clear dwell starts, because the one piece of
   *  data is still one reading. Absent: every key it can speak for is
   *  read. */
  stale?: (key: string) => boolean;
}

const fresh = <A>(source: string, about: A | undefined): Entry<A> => ({
  source,
  about,
  firstTrueAt: null,
  raised: false,
  firstFalseAt: null,
  clearedAt: null,
  clearedRunFrom: null,
  unreadAt: null,
});

function step<A>(e: Entry<A>, active: boolean, t: number): Entry<A> {
  if (active) {
    if (e.raised) return { ...e, firstFalseAt: null, unreadAt: null };
    const firstTrueAt = e.firstTrueAt ?? t;
    // Decided HERE, on reading times, and never in the view on the clock.
    // That is what makes it "across two readings": the reading that opened
    // the run and this one, at least 10 s later. One reading spans 0 s, so
    // it can never raise, however long nothing else arrives.
    const raised = t - firstTrueAt >= RAISE_AFTER_MS;
    return {
      ...e,
      firstTrueAt,
      raised,
      firstFalseAt: null,
      // A new raise retires the previous "Cleared at".
      clearedAt: raised ? null : e.clearedAt,
      clearedRunFrom: raised ? null : e.clearedRunFrom,
    };
  }
  if (!e.raised) return { ...e, firstTrueAt: null };
  const firstFalseAt = e.firstFalseAt ?? t;
  if (t - firstFalseAt >= CLEAR_AFTER_MS) {
    return { ...fresh(e.source, e.about), clearedAt: firstFalseAt, clearedRunFrom: e.firstTrueAt };
  }
  return { ...e, firstFalseAt, unreadAt: null };
}

/** A reading that cannot speak for this key (A7): a raised key stays up
 *  and any clear dwell is void; anything else is left as it is. */
function hold<A>(e: Entry<A>, t: number): Entry<A> {
  if (!e.raised) return e;
  return { ...e, firstFalseAt: null, unreadAt: e.unreadAt ?? t };
}

/** Fold one reading from one source into the state. */
export function advance<A>(state: DebounceState<A>, reading: Reading<A>): DebounceState<A> {
  const last = state.lastReadingAt.get(reading.source);
  // Not newer than the last reading from this source: the same reading
  // again, or an out-of-order one. Counting it would make one reading two.
  if (last !== undefined && reading.atMs <= last) return state;

  const entries = new Map(state.entries);
  const active = reading.active;
  for (const key of active.keys()) {
    if (reading.stale?.(key)) continue;
    const about = active instanceof Map ? active.get(key) : undefined;
    const e = entries.get(key) ?? fresh<A>(reading.source, about);
    const next = step(e, true, reading.atMs);
    // Shown, but by a reading that cannot vouch for it (A17b): up, stamped
    // since it could not be read, and still about what the last reading
    // that could vouch for it said.
    const unvouched = next.raised && reading.cannotRead?.(key) === true;
    entries.set(
      key,
      unvouched
        ? { ...next, unreadAt: e.unreadAt ?? reading.atMs, about: e.about ?? about }
        : { ...next, about: about ?? e.about },
    );
  }
  for (const [key, e] of state.entries) {
    if (e.source !== reading.source) continue;
    if (reading.stale?.(key)) {
      if (e.raised && e.unreadAt !== null) entries.set(key, { ...e, unreadAt: null });
      continue;
    }
    if (active.has(key)) continue;
    const next = reading.cannotRead?.(key) ? hold(e, reading.atMs) : step(e, false, reading.atMs);
    const idle =
      !next.raised &&
      next.firstTrueAt === null &&
      (next.clearedAt === null || reading.atMs - next.clearedAt >= CLEARED_SHOWN_FOR_MS);
    if (idle) entries.delete(key);
    else entries.set(key, next);
  }
  const lastReadingAt = new Map(state.lastReadingAt);
  lastReadingAt.set(reading.source, reading.atMs);
  return { entries, lastReadingAt };
}

/** What a source can be read for now, with no new reading (`glance`). */
export interface Glance {
  source: string;
  /** When this page saw it change (device clock). */
  atMs: number;
  /** As `Reading.cannotRead`, now. */
  cannotRead: (key: string) => boolean;
}

/**
 * Fold a GLANCE: what a source can be read for changed on the clock, with
 * no new reading -- a robot going dark, or back in view, on W3's presence
 * alone (A17b). It reads nothing, so it raises nothing, clears nothing and
 * is never a second reading of the data already read (the A17a double
 * count). It only dates the hold: a raised key it cannot read is stamped
 * from now, as a reading would stamp it (any clear dwell void, A7), so a
 * card held "as it was when it went dark" (A11) freezes at the flip and
 * not at the next reading; and a raised key it can read again loses its
 * stamp, so going dark again later freezes there, not at a spell that
 * ended. Every other entry is left exactly as it was.
 */
export function glance<A>(state: DebounceState<A>, seen: Glance): DebounceState<A> {
  const last = state.lastReadingAt.get(seen.source);
  if (last !== undefined && seen.atMs <= last) return state;
  const entries = new Map(state.entries);
  for (const [key, e] of state.entries) {
    if (e.source !== seen.source || !e.raised) continue;
    if (seen.cannotRead(key)) entries.set(key, hold(e, seen.atMs));
    else if (e.unreadAt !== null) entries.set(key, { ...e, unreadAt: null });
  }
  // In order with the readings: the next one is newer than this.
  const lastReadingAt = new Map(state.lastReadingAt);
  lastReadingAt.set(seen.source, seen.atMs);
  return { entries, lastReadingAt };
}

/**
 * One key's place on screen.
 *
 *   active    raised, and the last reading showed it -- or could not
 *             speak for it (A7), shown or not (A17b): then
 *             `unreadSinceMs` says since when.
 *   clearing  raised, and readings since `lookedFixedAtMs` no longer show
 *             it, but the 30 s clear dwell is not complete (A4).
 *   cleared   the dwell completed at the reading of `clearedAtMs`'s run;
 *             shown for 2 minutes from `clearedAtMs`.
 *
 * `seenSinceMs` is always the start of the run that raised it: for
 * `cleared`, the run the clear ended.
 */
export type DebouncedStatus<A = unknown> =
  | { status: 'active'; seenSinceMs: number; unreadSinceMs?: number; about?: A }
  | { status: 'clearing'; seenSinceMs: number; lookedFixedAtMs: number; about?: A }
  | { status: 'cleared'; clearedAtMs: number; seenSinceMs: number; about?: A };

/** What should be on screen at `nowMs`, by key. Keys still dwelling (not
 *  yet raised, showing again after a clear, or cleared more than 2 min
 *  ago) are absent. */
export function debouncedView<A>(state: DebounceState<A>, nowMs: number): Map<string, DebouncedStatus<A>> {
  const out = new Map<string, DebouncedStatus<A>>();
  for (const [key, e] of state.entries) {
    const about = e.about === undefined ? {} : { about: e.about };
    if (e.raised && e.firstTrueAt !== null) {
      const unread = e.unreadAt === null ? {} : { unreadSinceMs: e.unreadAt };
      out.set(
        key,
        e.firstFalseAt === null
          ? { status: 'active', seenSinceMs: e.firstTrueAt, ...unread, ...about }
          : { status: 'clearing', seenSinceMs: e.firstTrueAt, lookedFixedAtMs: e.firstFalseAt, ...about },
      );
    } else if (
      e.clearedAt !== null &&
      e.clearedRunFrom !== null &&
      // Showing again: "Cleared at" would be false. It is up again when it
      // raises again.
      e.firstTrueAt === null &&
      nowMs - e.clearedAt < CLEARED_SHOWN_FOR_MS
    ) {
      out.set(key, { status: 'cleared', clearedAtMs: e.clearedAt, seenSinceMs: e.clearedRunFrom, ...about });
    }
  }
  return out;
}
